import type { Issue, Vec3 } from './model.js';
import type { ProjectDocument, QuarterTurn } from './document.js';
import type { EvaluatedCabinet, EvaluatedProject } from './project.js';
import { partBox } from './geometry.js';
import { buildRoom, wallTopAt, type RoomWallBuild } from './room.js';
import { buildAssembly } from './assembly.js';
import { isLegPart, trimOfPart } from './trims.js';
import type { AssemblySlope } from './slope.js';

/**
 * "Montiraj na zid": a unit (element or assembly) goes with its back against the inner face of a wall.
 * It turns by quarter turns so its front faces into the room, keeps its height, and takes the freest
 * place along the wall — the middle of the wall when it is free, otherwise the middle of the widest
 * gap between the other units and the openings (doors always, windows when the unit reaches them).
 */

const NEAR = 50_000; // units closer than 50 mm to the wall stand at it

export interface WallMount {
  rotationY: QuarterTurn;
  position: Vec3;
  /** Set when the wall is not at a right angle to the grid (the unit is turned to the nearest quarter). */
  warning?: string;
}

/**
 * World box of a unit from its real parts (assemblies may have panels on either side of their origin),
 * with a given turn and position; same transform as the viewer (mirror about the bounds width, then the turn).
 */
export function unitWorldBoxAt(c: EvaluatedCabinet, rotationY: QuarterTurn, position: Vec3): { min: Vec3; max: Vec3 } {
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of c.result.parts) {
    const b = partBox(p);
    for (const k of [0, 1, 2] as const) {
      min[k] = Math.min(min[k], b.min[k]);
      max[k] = Math.max(max[k], b.min[k] + b.size[k]);
    }
  }
  if (!Number.isFinite(min[0])) return { min: [...position], max: [...position] };
  const w = c.result.bounds.size[0];
  const xs = c.instance.transform.mirrored ? [w - max[0], w - min[0]] : [min[0], max[0]];
  const phi = (-rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  const wx: number[] = [];
  const wz: number[] = [];
  for (const x of xs)
    for (const z of [min[2], max[2]]) {
      wx.push(position[0] + x * cos + z * sin);
      wz.push(position[2] - x * sin + z * cos);
    }
  return { min: [Math.min(...wx), position[1] + min[1], Math.min(...wz)], max: [Math.max(...wx), position[1] + max[1], Math.max(...wz)] };
}

/** World box corners in plan (x, z). */
const footprint = (b: { min: Vec3; max: Vec3 }) => [
  [b.min[0], b.min[2]],
  [b.max[0], b.min[2]],
  [b.max[0], b.max[2]],
  [b.min[0], b.max[2]],
];

/** Extent of a world box along the wall (u from the wall start) and towards the room (depth from the inner face). */
function onWall(w: RoomWallBuild, b: { min: Vec3; max: Vec3 }) {
  const us = footprint(b).map(([x, z]) => (x! - w.ax) * w.ux + (z! - w.az) * w.uz);
  // Distance into the room (−n side) from the inner face.
  const ds = footprint(b).map(([x, z]) => -((x! - w.ax) * w.nx + (z! - w.az) * w.nz));
  return { u0: Math.min(...us), u1: Math.max(...us), d0: Math.min(...ds), d1: Math.max(...ds) };
}

export function mountOnWall(doc: ProjectDocument, ev: EvaluatedProject, unitId: string, lineId: string): WallMount | { error: string } {
  const plan = doc.roomPlan;
  if (!plan) return { error: 'Projekat nema prostoriju.' };
  const w = buildRoom(plan).walls.find((x) => x.lineId === lineId);
  if (!w) return { error: 'Izabrana linija nije zid.' };
  const unit = ev.cabinets.find((c) => c.instance.id === unitId);
  if (!unit) return { error: 'Element ne postoji.' };
  // Front of the unit (local +z) in the world after a turn φ = −rotationY: (sin φ, cos φ). It must look into the room (−n).
  let best: { rot: QuarterTurn; dot: number } = { rot: 0, dot: -Infinity };
  for (const rot of [0, 90, 180, 270] as QuarterTurn[]) {
    const phi = (-rot * Math.PI) / 180;
    const dot = Math.sin(phi) * -w.nx + Math.cos(phi) * -w.nz;
    if (dot > best.dot + 1e-9) best = { rot, dot };
  }
  const y = unit.instance.transform.position[1];
  const at0 = unitWorldBoxAt(unit, best.rot, [0, y, 0]);
  const e = onWall(w, at0);
  const width = e.u1 - e.u0;
  const depth = e.d1 - e.d0;
  const bottom = at0.min[1];
  const top = at0.max[1];
  // Obstacles along the wall: other units standing at it, doors, windows the unit would cover.
  const busy: [number, number][] = [];
  for (const c of ev.cabinets) {
    if (c.instance.id === unitId || c.instance.hidden) continue;
    const b = unitWorldBoxAt(c, c.instance.transform.rotationY, c.instance.transform.position);
    const o = onWall(w, b);
    if (o.d0 <= NEAR && o.d1 > 0 && o.d0 < depth && Math.min(b.max[1], top) > Math.max(b.min[1], bottom)) busy.push([o.u0, o.u1]);
  }
  for (const it of plan.items) {
    if (it.lineId !== lineId || (it.kind !== 'door' && it.kind !== 'window')) continue;
    const span = buildRoom(plan).items.find((x) => x.item.id === it.id);
    if (!span) continue;
    if (it.kind === 'door' || Math.min(top, it.elevation + it.height) > Math.max(bottom, it.elevation)) busy.push([span.u0, span.u1]);
  }
  busy.sort((a, b) => a[0] - b[0]);
  const gaps: [number, number][] = [];
  let cur = 0;
  for (const [a, b] of busy) {
    if (a > cur) gaps.push([cur, Math.min(a, w.length)]);
    cur = Math.max(cur, b);
  }
  if (cur < w.length) gaps.push([cur, w.length]);
  const fits = gaps.filter(([a, b]) => b - a >= width - 1);
  const pick = fits.length ? fits.reduce((p, q) => (q[1] - q[0] > p[1] - p[0] + 1 ? q : p)) : null;
  const centre = pick ? (pick[0] + pick[1]) / 2 : w.length / 2;
  // Move so the back touches the inner face (depth 0) and the unit is centred at `centre`.
  const du = centre - (e.u0 + e.u1) / 2;
  const dd = -e.d0;
  const dx = w.ux * du - w.nx * dd;
  const dz = w.uz * du - w.nz * dd;
  const warnings: string[] = [];
  if (best.dot < 0.999) warnings.push('Zid nije pod pravim uglom — element je okrenut najbliže (samo okreti od 90°).');
  if (!pick) warnings.push('Na zidu nema dovoljno slobodnog mjesta — element je na sredini zida.');
  if (top > Math.min(wallTopAt(w, Math.max(0, centre - width / 2)), wallTopAt(w, Math.min(w.length, centre + width / 2))) + 500) warnings.push('Element je viši od zida na tom mjestu.');
  return { rotationY: best.rot, position: [Math.round(dx), y, Math.round(dz)], ...(warnings.length ? { warning: warnings.join(' ') } : {}) };
}

// ---------- fit to a sloped wall ----------

/**
 * A free-panel assembly standing at a wall whose top slopes (different corner heights) and reaching
 * above that slope gets the exact "sklop pod kosinom" for it: its top face cut along the wall's top
 * line, in the assembly's own axis that runs along the wall. Null when it does not hit a slope, when
 * the wall is not along one of its axes, or when it already has that slope.
 */
export function wallSlopeFit(doc: ProjectDocument, ev: EvaluatedProject, unitId: string): { slope: AssemblySlope; lineId: string } | { remove: true } | null {
  const plan = doc.roomPlan;
  const inst = doc.assemblies.find((a) => a.id === unitId);
  const evc = ev.cabinets.find((c) => c.instance.id === unitId);
  if (!inst || !evc || inst.hidden) return null;
  // A slope made from a wall that no wall needs any more (moved away / under the slope): offer to drop it.
  const noLongerNeeded = inst.slope?.fromWall ? ({ remove: true } as const) : null;
  if (!plan?.wallHeight) return noLongerNeeded;
  // The carcass as built without a slope (plinth, fillers and legs left out).
  const plain = buildAssembly({ ...inst, slope: undefined });
  const body = plain.parts.filter((p) => !trimOfPart(inst, p.localKey) && !isLegPart(p.localKey));
  if (!body.length) return noLongerNeeded;
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of body) {
    const b = partBox(p);
    for (const k of [0, 1, 2] as const) {
      min[k] = Math.min(min[k], b.min[k]);
      max[k] = Math.max(max[k], b.min[k] + b.size[k]);
    }
  }
  const t = inst.transform;
  const w0 = plain.bounds.size[0];
  const phi = (-t.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  const world = (x: number, z: number): [number, number] => {
    const xm = t.mirrored ? w0 - x : x;
    return [t.position[0] + xm * cos + z * sin, t.position[2] - xm * sin + z * cos];
  };
  const top = max[1];
  for (const w of buildRoom(plan).walls) {
    const u = (p: [number, number]) => (p[0] - w.ax) * w.ux + (p[1] - w.az) * w.uz;
    const d = (p: [number, number]) => -((p[0] - w.ax) * w.nx + (p[1] - w.az) * w.nz);
    const foot = [world(min[0], min[2]), world(max[0], min[2]), world(max[0], max[2]), world(min[0], max[2])];
    const us = foot.map(u);
    const ds = foot.map(d);
    // At the wall: its footprint reaches the inner face (± 50 mm) and lies along the wall.
    if (Math.min(...ds) > NEAR || Math.max(...ds) < 0 || Math.max(...us) <= 0 || Math.min(...us) >= w.length) continue;
    // The roof over this wall (the planes through it and its joined walls; the lowest counts), so a
    // roof sloping along the wall (gable) and one sloping away from it (low knee wall) both work.
    const roofs = [w.ceilA, w.ceilB].filter((c): c is NonNullable<typeof c> => !!c);
    const planes = roofs.length ? roofs : [w.ceil];
    const roofAt = (p: [number, number]) => Math.min(...planes.map((c) => c.a * p[0] + c.b * p[1] + c.c)) - t.position[1];
    // Roof height over the unit's top corners (in its height frame): it must change along one of its
    // own horizontal axes only (the slope cut runs across the other).
    const hx0 = roofAt(world(min[0], min[2]));
    const hx1 = roofAt(world(max[0], min[2]));
    const hz1 = roofAt(world(min[0], max[2]));
    const alongX = Math.abs(hx1 - hx0);
    const alongZ = Math.abs(hz1 - hx0);
    if (Math.max(alongX, alongZ) < 1000) continue; // level roof: no slope
    const along: 'x' | 'z' = alongX >= alongZ ? 'x' : 'z';
    if (Math.min(alongX, alongZ) > 2000) continue; // roof at an angle to the unit
    const k = along === 'x' ? 0 : 2;
    const hLo = hx0;
    const hHi = along === 'x' ? hx1 : hz1;
    if (top <= Math.min(hLo, hHi) + 1000) continue; // under the slope
    const len = max[k] - min[k];
    const low = hLo < hHi ? 'start' : 'end';
    const hl = Math.min(hLo, hHi);
    const hh = Math.max(hLo, hHi);
    const drop = Math.round(top - hl);
    const run = Math.round((len * (top - hl)) / (hh - hl));
    const slope: AssemblySlope = { face: 'top', axis: along, [low]: { run, drop }, closePanels: inst.slope?.closePanels ?? true, ...(inst.slope?.oversize ? { oversize: inst.slope.oversize } : {}), fromWall: true };
    const cur = inst.slope;
    const same = cur && cur.face === 'top' && cur.axis === along && !cur[low === 'start' ? 'end' : 'start'] && cur[low] && Math.abs(cur[low]!.run - run) <= 1000 && Math.abs(cur[low]!.drop - drop) <= 1000;
    if (same) return null;
    return { slope, lineId: w.lineId };
  }
  return noLongerNeeded;
}

// ---------- units inside walls ----------

/** How far a unit reaches into a wall past its inner face (µm, > 0), with the wall; null when not. */
function intoWall(c: EvaluatedCabinet, w: RoomWallBuild): number | null {
  const t = c.instance.transform;
  const b = unitWorldBoxAt(c, t.rotationY, t.position);
  const corners: [number, number][] = [
    [b.min[0], b.min[2]],
    [b.max[0], b.min[2]],
    [b.max[0], b.max[2]],
    [b.min[0], b.max[2]],
  ];
  const into = corners.map(([x, z]) => (x - w.ax) * w.nx + (z - w.az) * w.nz);
  const along = corners.map(([x, z]) => (x - w.ax) * w.ux + (z - w.az) * w.uz);
  const deepest = Math.max(...into);
  // Past the inner face (> 1 mm), not wholly behind the wall, along the wall's span, below its top.
  if (deepest <= 1000 || Math.min(...into) >= w.thickness) return null;
  if (Math.max(...along) <= 1000 || Math.min(...along) >= w.length - 1000) return null;
  if (b.min[1] >= Math.max(w.hA, w.hB)) return null;
  return deepest;
}

/** Warnings for units that go into a room wall (through its thickness). */
export function unitsInWalls(doc: ProjectDocument, cabinets: EvaluatedCabinet[]): Issue[] {
  const plan = doc.roomPlan;
  if (!plan?.wallHeight) return [];
  const walls = buildRoom(plan).walls;
  const order = plan.lines.filter((l) => l.kind === 'wall').map((l) => l.id);
  const out: Issue[] = [];
  for (const c of cabinets) {
    if (c.instance.hidden) continue;
    for (const w of walls) {
      const d = intoWall(c, w);
      if (d === null) continue;
      out.push({
        severity: 'warning',
        code: 'room.unit-in-wall',
        message: `${c.instance.name} ulazi ${Math.round(d / 1000)} mm u Zid ${order.indexOf(w.lineId) + 1}.`,
        cabinetId: c.instance.id,
        ref: w.lineId,
      });
    }
  }
  return out;
}

/** Moves a unit out of a wall: back by its depth into the wall, across the wall's inner face. */
export function moveOutOfWall(doc: ProjectDocument, ev: EvaluatedProject, unitId: string, lineId: string): boolean {
  const c = ev.cabinets.find((x) => x.instance.id === unitId);
  const w = doc.roomPlan ? buildRoom(doc.roomPlan).walls.find((x) => x.lineId === lineId) : undefined;
  const u = [...doc.cabinets, ...doc.assemblies].find((x) => x.id === unitId);
  if (!c || !w || !u || u.locked) return false;
  const d = intoWall(c, w);
  if (d === null) return false;
  u.transform.position[0] -= Math.round(w.nx * d);
  u.transform.position[2] -= Math.round(w.nz * d);
  return true;
}
